import React, { useEffect, useRef, useState } from 'react';
import { App, Alert, Button, Image, Upload } from 'antd';
import { DeleteOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import { withDevModalFilePath } from '../common';

const { Dragger } = Upload;
const { Modal } = withDevModalFilePath('client/src/components/location/LocationMapImageSettings.tsx');

export interface LocationMapImageSettingsProps {
  locationId: string;
  canEdit: boolean;
}

const LocationMapImageSettings: React.FC<LocationMapImageSettingsProps> = ({ locationId, canEdit }) => {
  const { message } = App.useApp();
  const utils = trpc.useUtils();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const localPreviewUrlRef = useRef<string | null>(null);
  const pendingFileIdRef = useRef<string | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState<string | null>(null);

  const { data: imageData } = trpc.location.getMapImage.useQuery(
    { id: locationId },
    { enabled: !!locationId }
  );

  useEffect(() => {
    if (imageData?.imageUrl) {
      const matchesPending =
        !pendingFileIdRef.current || imageData.fileId === pendingFileIdRef.current;
      if (matchesPending) {
        setDisplayImageUrl(imageData.imageUrl);
        if (localPreviewUrlRef.current) {
          URL.revokeObjectURL(localPreviewUrlRef.current);
          localPreviewUrlRef.current = null;
        }
        pendingFileIdRef.current = null;
      }
    } else if (!pendingFileIdRef.current && !localPreviewUrlRef.current) {
      setDisplayImageUrl(null);
    }
  }, [imageData?.imageUrl, imageData?.fileId]);

  useEffect(
    () => () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
    },
    []
  );

  const getUploadUrlMutation = trpc.file.getUploadUrl.useMutation();
  const registerFileMutation = trpc.file.registerFile.useMutation();
  const verifyUploadMutation = trpc.file.verifyUpload.useMutation();

  const setImageMutation = trpc.location.setMapImage.useMutation({
    onSuccess: async () => {
      await utils.location.getMapImage.invalidate({ id: locationId });
      await utils.location.getTreeWithCounts.invalidate();
    },
    onError: (error) => {
      pendingFileIdRef.current = null;
      message.error(`画像の更新に失敗しました: ${error.message}`);
    },
  });

  const removeImageMutation = trpc.location.removeMapImage.useMutation({
    onSuccess: async () => {
      message.success('マップ画像を削除しました');
      await utils.location.getMapImage.invalidate({ id: locationId });
      await utils.location.getTreeWithCounts.invalidate();
    },
    onError: (error) => {
      message.error(`画像の削除に失敗しました: ${error.message}`);
    },
  });

  const computeAverageHexColor = async (file: File): Promise<string | null> => {
    const canvasSize = 32;
    const canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return null;
    }

    const toHex2 = (v: number) => v.toString(16).padStart(2, '0');

    let bitmap: ImageBitmap | null = null;
    let objectUrl: string | null = null;

    try {
      if (typeof createImageBitmap === 'function') {
        bitmap = await createImageBitmap(file);
        ctx.drawImage(bitmap, 0, 0, canvasSize, canvasSize);
      } else {
        objectUrl = URL.createObjectURL(file);
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = document.createElement('img');
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error('Failed to load image'));
          el.src = objectUrl as string;
        });
        ctx.drawImage(img, 0, 0, canvasSize, canvasSize);
      }

      const { data } = ctx.getImageData(0, 0, canvasSize, canvasSize);

      let r = 0;
      let g = 0;
      let b = 0;
      let count = 0;

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha < 16) {
          continue;
        }
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count += 1;
      }

      if (!count) {
        return null;
      }

      const rr = Math.round(r / count);
      const gg = Math.round(g / count);
      const bb = Math.round(b / count);
      return `#${toHex2(rr)}${toHex2(gg)}${toHex2(bb)}`;
    } catch {
      return null;
    } finally {
      if (bitmap && typeof bitmap.close === 'function') {
        bitmap.close();
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  };

  const handleUpload = async (file: File) => {
    const previousImageUrl = displayImageUrl ?? null;
    try {
      setUploading(true);

      const averageColorPromise = computeAverageHexColor(file);

      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      localPreviewUrlRef.current = previewUrl;
      setDisplayImageUrl(previewUrl);

      const presignedData = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category: 'location-map',
      });

      const formData = new FormData();
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      try {
        await fetch(presignedData.url, {
          method: 'POST',
          body: formData,
          mode: 'no-cors',
        });
      } catch {
        throw new Error('S3 upload failed. Please check your network connection and try again.');
      }

      try {
        await verifyUploadMutation.mutateAsync({ key: presignedData.key });
      } catch {
        throw new Error('S3 upload verification failed. Please confirm the bucket CORS configuration.');
      }

      const fileRecord = await registerFileMutation.mutateAsync({
        s3Key: presignedData.key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        category: 'location-map',
        associations: [
          {
            entityType: 'location',
            entityId: locationId,
            associationType: 'location-map',
          },
        ],
      });

      pendingFileIdRef.current = fileRecord.id;

      await setImageMutation.mutateAsync({
        id: locationId,
        fileId: fileRecord.id,
        mapBackgroundColor: await averageColorPromise,
      });

      let serverImageApplied = false;
      try {
        const latestImage = await utils.location.getMapImage.fetch({ id: locationId });
        if (latestImage && latestImage.fileId === fileRecord.id) {
          setDisplayImageUrl(latestImage.imageUrl);
          await utils.location.getMapImage.setData({ id: locationId }, latestImage);
          pendingFileIdRef.current = null;
          serverImageApplied = true;
        }
      } catch {
        // ignore
      } finally {
        if (serverImageApplied && localPreviewUrlRef.current) {
          URL.revokeObjectURL(localPreviewUrlRef.current);
          localPreviewUrlRef.current = null;
        }
      }

      message.success('画像をアップロードしました');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '予期しないエラーが発生しました。';
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
      pendingFileIdRef.current = null;
      setDisplayImageUrl(previousImageUrl);
      message.error(`アップロードに失敗しました: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    Modal.confirm({
      title: 'マップ画像を削除',
      content: '本当にマップ画像を削除しますか？削除すると画像なし（グリッド表示）になります。',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        await removeImageMutation.mutateAsync({ id: locationId });
      },
    });
  };

  return (
    <div>
      <Alert
        message="マップ画像について"
        description="このエリアのレイアウト図（画像）をアップロードできます。推奨: 横1000px × 縦500-600px程度。"
        type="info"
        showIcon
        className="mb-4"
      />

      <div className="mb-6">
        <div className="text-base font-semibold mb-3">現在の画像</div>
        {displayImageUrl ? (
          <div className="relative">
            <div
              style={{
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                backgroundColor: '#fafafa',
              }}
            >
              <Image
                src={displayImageUrl}
                alt="Location map"
                style={{ maxHeight: 320, objectFit: 'contain' }}
                preview={false}
              />
            </div>
            <div className="mt-3 flex gap-2 justify-center">
              <Button
                icon={<EyeOutlined />}
                onClick={() => setPreviewOpen(true)}
                disabled={!displayImageUrl}
              >
                プレビュー
              </Button>
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={handleRemove}
                disabled={!canEdit || uploading || removeImageMutation.isPending}
              >
                削除
              </Button>
            </div>
          </div>
        ) : (
          <div
            style={{
              border: '2px dashed #d9d9d9',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              backgroundColor: '#fafafa',
              color: '#666',
            }}
          >
            画像が設定されていません（グリッド表示になります）
          </div>
        )}
      </div>

      <Dragger
        name="file"
        multiple={false}
        accept="image/*"
        showUploadList={false}
        disabled={!canEdit || uploading}
        beforeUpload={(file) => {
          void handleUpload(file);
          return false;
        }}
        style={{ padding: 12 }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          クリックまたはドラッグ&ドロップで画像をアップロード
        </p>
        <p className="ant-upload-hint">PNG/JPG 推奨。アップロード後すぐに反映されます。</p>
      </Dragger>

      <Image
        src={displayImageUrl ?? undefined}
        style={{ display: 'none' }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
      />
    </div>
  );
};

export default LocationMapImageSettings;

